// src\components\Payroll\PartialSalaryFld\shared\PartialSalaryModal.js
import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";

import { Select } from "../../../../common/Select";
import { Input } from "../../../../common/Input";
import { PositionPickerField } from "../../../../common/selectComponents/PositionPicker";
import { ConfirmationModal } from "../../../../common/Modal";
import { LoadingSpinner } from "../../../../common/Loading";

// ── Empty form ────────────────────────────────────────────────────────────────
const emptyForm = {
  amount: "",
  employeeTypeId: "",
  positionId: "",
  positionCode: "",
  positionDescription: "",
  isActive: true,
};

// ── Modal ─────────────────────────────────────────────────────────────────────
export const PartialSalaryModal = ({
  isOpen,
  onClose,
  mode,           // "add" | "edit"
  selectedData,
  options,
  onAdd,
  onUpdate,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState({
    title: "",
    message: "",
    action: null,
  });

  // ── Sync form when modal opens ────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setErrors({});
      if (mode === "edit" && selectedData) {
        setForm({
          ...emptyForm,
          amount: selectedData.rawAmount || "",
          employeeTypeId: selectedData.employeeTypeId || "",
          positionId: selectedData.positionId || "",
          positionCode: selectedData.positionCode || "",
          positionDescription: selectedData.positionDescription || "",
          isActive: selectedData.isActive ?? true,
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [isOpen, mode, selectedData]);

  // ── Field setter — clears error on change ─────────────────────────────────
  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // ── Position picker handlers ──────────────────────────────────────────────
  const handlePositionSelect = (position) => {
    set("positionId", position.id);
    set("positionCode", position.code);
    set("positionDescription", position.description);
  };

  const handlePositionClear = () => {
    set("positionId", "");
    set("positionCode", "");
    set("positionDescription", "");
  };

  // ── Validate ──────────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!form.amount) {
      e.amount = "Amount is required.";
    } else if (isNaN(parseFloat(form.amount)) || parseFloat(form.amount) <= 0) {
      e.amount = "Amount must be a valid number greater than 0.";
    }
    if (!form.employeeTypeId) e.employeeTypeId = "Employee Type is required.";
    if (!form.positionId) e.positionId = "Position is required.";
    return e;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    if (mode === "add") {
      setConfirmationConfig({
        title: "Create Partial Salary",
        message: "Save this partial salary?",
        action: doAdd,
      });
    } else {
      setConfirmationConfig({
        title: "Update Partial Salary",
        message: "Update this partial salary?",
        action: doUpdate,
      });
    }
    setShowConfirmation(true);
  };

  const doAdd = async () => {
    setConfirmLoading(true);
    await onAdd(form, () => { setShowConfirmation(false); onClose(); });
    setConfirmLoading(false);
  };

  const doUpdate = async () => {
    setConfirmLoading(true);
    await onUpdate(form, selectedData.id, () => { setShowConfirmation(false); onClose(); });
    setConfirmLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto">
      <div className="bg-white w-full md:w-[90%] lg:w-[50%] xl:w-[40%] rounded-lg shadow-2xl flex flex-col h-auto max-h-[90vh]">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-3 border-b bg-gray-50 rounded-t-lg">
          <h2 className="text-lg font-bold text-blue-900 flex items-center gap-2">
            <MonetizationOnIcon />
            {mode === "add" ? "Add Partial Salary" : "Edit Partial Salary"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-red-500 bg-transparent border-none cursor-pointer"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-grow bg-white space-y-4">
          <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Partial Salary Information
              </span>
            </div>

            <div className="space-y-4">
              <Input
                label="Amount:"
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                placeholder="Enter amount"
                isRequired={true}
                error={!!errors.amount}
                errorMessage={errors.amount}
              />

              <Select
                label="Employee Type:"
                options={options?.employeeTypes || []}
                value={form.employeeTypeId || ""}
                onChange={(e) => set("employeeTypeId", e.target.value)}
                isRequired={true}
                error={!!errors.employeeTypeId}
                errorMessage={errors.employeeTypeId}
              />

              <PositionPickerField
                label="Position:"
                positionId={form.positionId}
                displayValue={form.positionDescription}
                onSelect={handlePositionSelect}
                onClear={handlePositionClear}
                isRequired={true}
                error={!!errors.positionId}
                errorMessage={errors.positionId}
                allowClear={false}
              />

              {/* IsActive checkbox */}
              <div className="flex items-center pt-2">
                <input
                  type="checkbox"
                  id="isActive-partial-salary"
                  checked={form.isActive}
                  onChange={(e) => set("isActive", e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label htmlFor="isActive-partial-salary" className="ml-2 text-sm font-medium text-gray-600">
                  Active
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-end items-center gap-2 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-400 text-white rounded cursor-pointer hover:bg-gray-500 border-none transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-8 py-2 bg-blue-600 text-white rounded font-bold cursor-pointer hover:bg-blue-700 border-none transition-colors"
          >
            {mode === "add" ? "Save" : "Update"}
          </button>
        </div>
      </div>

      <ConfirmationModal
        visible={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={confirmationConfig.action}
        title={confirmationConfig.title}
        message={confirmationConfig.message}
        isLoading={confirmLoading}
      />
    </div>
  );
};